import type { ComponentType, ReactNode, SVGProps } from 'react';

import { cn } from '@/lib/utils';

/**
 * The one empty-state layout: a sketch, a title, one line saying what to do, and the button that
 * does it — for a page or section that is empty because nothing exists YET. When filters hide
 * everything, say THAT (LostArt + Clear filters) instead of inviting them to create something
 * that may already exist. Not for small dashboard cards: a sketch in every box is clutter.
 * `sm` is for a section inside a card (comments, files).
 */
export function EmptyState({
  art: Art,
  title,
  body,
  children,
  size = 'lg',
  className,
}: {
  art: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  body?: ReactNode;
  /** The action(s) that fix the emptiness. */
  children?: ReactNode;
  size?: 'lg' | 'sm';
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center px-4 text-center', size === 'lg' ? 'gap-2 py-12' : 'gap-1.5 py-6', className)}>
      <Art className={cn('w-auto', size === 'lg' ? 'mb-2 h-24' : 'mb-1 h-14')} />
      <p className={cn('font-semibold', size === 'lg' ? 'text-base' : 'text-sm')}>{title}</p>
      {body && <p className="max-w-sm text-sm text-muted-foreground">{body}</p>}
      {children && <div className="flex flex-wrap justify-center gap-2 pt-2">{children}</div>}
    </div>
  );
}
